import { ONE_DAY_SEC, unixNowSec } from './dateTime';
import { someoneWasSignedUpOnThisMachineInLast30Days } from './signupTracking';

type StickyType = 'exit' | 'scroll';

/**
 * Adds classes to body so the sticky of specified type appears and is rendered
 * correctly.
 *
 * The OnExit sticky is rendered full-page, while the OnScroll sticky is
 * rendered with auto-height.
 *
 * The class given to the sticky in combination with styles in _sticky.scss
 * achieve this.
 *
 * @param type Type of sticky to appear
 */
const showSticky = (type: StickyType): void => {
  document.body.dataset.stickyType = type;
  document.body.classList.add(`sticky-${type}`);
  document.body.classList.add('sticky-visible');
};

/**
 * Resets classes on body element so the sticky no longer apears
 */
const hideSticky = (): void => {
  document.body.classList.remove('sticky-exit');
  document.body.classList.remove('sticky-scroll');
  document.body.classList.remove('sticky-visible');
};

/**
 * Utility function used to compute proper local storage key for the specified
 * sticky type
 * @param type Type of sticky to get the local storage key for
 * @returns Local storage key for the specified sticky type
 */
const stickyLocalStorageKey = (
  type: StickyType,
): 'sticky-exit-hidden-until' | 'sticky-scroll-hidden-until' =>
  `sticky-${type}-hidden-until`;

/**
 * Determines if the local storage key preventing the sticky of the specified
 * type from reapearing has expired.
 * @param type Type of sticky
 * @returns Boolean indicating if sticky prevention for specified type  has expired
 */
const didPreventionExpire = (type: StickyType): boolean => {
  const key = stickyLocalStorageKey(type);

  const strValue = localStorage.getItem(key);
  if (strValue === null) {
    return true;
  }

  const intValue = parseInt(strValue);
  if (isNaN(intValue)) {
    return true;
  }
  const now = unixNowSec();
  return now >= intValue;
};

/**
 * Prevents sticky from reapearing by setting an expiry value to local storage
 * for that specific type of sticky.
 *
 * OnExit and OnScroll stickies have separate expiry values.
 */
const preventSticky = (type: StickyType): void => {
  localStorage.setItem(
    stickyLocalStorageKey(type),
    (unixNowSec() + ONE_DAY_SEC).toString(),
  );
};

/**
 * Sets up sticky to hide when the user clicks the close button on it.
 */
const initHideSticky = (): void => {
  const elements = document.querySelectorAll<HTMLElement>(
    '.sticky [data-dismiss="sticky"]',
  );

  elements.forEach((el) => {
    el.addEventListener('click', () => {
      const stickyType: StickyType =
        document.body.dataset.stickyType === 'exit' ? 'exit' : 'scroll';

      preventSticky(stickyType);
      hideSticky();
    });
  });
};

/**
 * @returns The amount, in pixels of how far down the page is scrolled
 */
const windowYScroll = () =>
  window.scrollY ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

/**
 * @returns Document/page height in pixels
 */
const documentHeight = () =>
  Math.max(
    document.documentElement.scrollHeight || 0,
    document.body.scrollHeight || 0,
    document.documentElement.offsetHeight || 0,
    document.body.offsetHeight || 0,
    document.documentElement.clientHeight || 0,
    document.body.clientHeight || 0,
  );

const scrollPercentage = () => (windowYScroll() / documentHeight()) * 100;

/**
 * Sets up sticky to show up when the user has scrolled far enough down on the
 * page.
 */
const initOnScrollDown = () => {
  window.addEventListener('scroll', () => {
    if (
      scrollPercentage() > 40 &&
      !someoneWasSignedUpOnThisMachineInLast30Days() &&
      didPreventionExpire('scroll')
    ) {
      showSticky('scroll');
    }
  });
};

/**
 * Sets up sticky to appear when user shows exit intent. This basically means
 * when user moves mouse to top border of the browser page.
 */
const initOnTabClose = () => {
  document.addEventListener('mouseleave', (e) => {
    if (
      e.clientY <= 0 &&
      !someoneWasSignedUpOnThisMachineInLast30Days() &&
      didPreventionExpire('exit')
    ) {
      showSticky('exit');
    }
  });
};

/**
 * Primary function to initialize sticky CTA behavior.
 * Sets up sticky to show up on scroll down, as well on user showing exit intent
 */
export const initSticky = (): void => {
  initHideSticky();
  initOnScrollDown();
  initOnTabClose();
};
